/**
 * Quad Cities service-area reference data (build-plan: service-area map).
 *
 * The interaction unit is a TOWN, not a raw ZIP — contractors think "I serve
 * Davenport," not "I serve 52801/52803/52806". Each town carries the full ZIP
 * set it expands to; ZIPs are the canonical matching key (see
 * contractor/api domain), towns are how a human picks them.
 *
 * Provenance: generated from the `zipcodes` npm dataset, filtered to the QC
 * metro bounding box (lat 41.30–41.80, lng -90.95–-90.20), grouped by town
 * with each marker placed at the mean of its ZIPs' distinct centroids. This
 * is static reference data — ZIP centroids and town names effectively never
 * change. Spot-checked: Davenport 52801, Bettendorf 52722, Coal Valley 61240,
 * Eldridge 52748, Moline 61265. To extend coverage, widen the box and
 * regenerate; do NOT hand-edit coordinates.
 */

export interface ServiceTown {
  readonly city: string;
  readonly state: "IA" | "IL";
  readonly lat: number;
  readonly lng: number;
  /** Every ZIP this town expands to — the canonical matching keys. */
  readonly zips: readonly string[];
}

export const QC_TOWNS: readonly ServiceTown[] = [
  { city: "Bettendorf", state: "IA", lat: 41.5509, lng: -90.4942, zips: ["52722"] },
  { city: "Blue Grass", state: "IA", lat: 41.5112, lng: -90.738, zips: ["52726"] },
  { city: "Buffalo", state: "IA", lat: 41.4564, lng: -90.7235, zips: ["52728"] },
  { city: "Camanche", state: "IA", lat: 41.7886, lng: -90.2709, zips: ["52730"] },
  {
    city: "Davenport",
    state: "IA",
    lat: 41.5518,
    lng: -90.5874,
    zips: ["52801", "52802", "52803", "52804", "52805", "52806", "52807", "52808", "52809"],
  },
  { city: "Dixon", state: "IA", lat: 41.7113, lng: -90.7553, zips: ["52745"] },
  { city: "Donahue", state: "IA", lat: 41.7093, lng: -90.6829, zips: ["52746"] },
  { city: "Durant", state: "IA", lat: 41.6145, lng: -90.91, zips: ["52747"] },
  { city: "Eldridge", state: "IA", lat: 41.6635, lng: -90.5634, zips: ["52748"] },
  { city: "Le Claire", state: "IA", lat: 41.6082, lng: -90.3628, zips: ["52753"] },
  { city: "Long Grove", state: "IA", lat: 41.7213, lng: -90.5534, zips: ["52756"] },
  { city: "Mc Causland", state: "IA", lat: 41.743, lng: -90.4539, zips: ["52758"] },
  { city: "Montpelier", state: "IA", lat: 41.4625, lng: -90.8088, zips: ["52759"] },
  { city: "New Liberty", state: "IA", lat: 41.7132, lng: -90.8599, zips: ["52765"] },
  { city: "Pleasant Valley", state: "IA", lat: 41.5746, lng: -90.4213, zips: ["52767"] },
  { city: "Princeton", state: "IA", lat: 41.6858, lng: -90.3706, zips: ["52768"] },
  { city: "Stockton", state: "IA", lat: 41.5914, lng: -90.8588, zips: ["52769"] },
  { city: "Walcott", state: "IA", lat: 41.6453, lng: -90.7256, zips: ["52773"] },
  { city: "Albany", state: "IL", lat: 41.7659, lng: -90.2081, zips: ["61230"] },
  { city: "Andalusia", state: "IL", lat: 41.4392, lng: -90.7176, zips: ["61232"] },
  { city: "Barstow", state: "IL", lat: 41.5147, lng: -90.3577, zips: ["61236"] },
  { city: "Buffalo Prairie", state: "IL", lat: 41.3368, lng: -90.8522, zips: ["61237"] },
  { city: "Carbon Cliff", state: "IL", lat: 41.4948, lng: -90.3907, zips: ["61239"] },
  { city: "Coal Valley", state: "IL", lat: 41.4351, lng: -90.4652, zips: ["61240"] },
  { city: "Colona", state: "IL", lat: 41.4885, lng: -90.321, zips: ["61241"] },
  { city: "Cordova", state: "IL", lat: 41.6928, lng: -90.3071, zips: ["61242"] },
  { city: "East Moline", state: "IL", lat: 41.5118, lng: -90.4321, zips: ["61244"] },
  { city: "Hampton", state: "IL", lat: 41.5559, lng: -90.4093, zips: ["61256"] },
  { city: "Hillsdale", state: "IL", lat: 41.5929, lng: -90.2263, zips: ["61257"] },
  { city: "Illinois City", state: "IL", lat: 41.3892, lng: -90.8925, zips: ["61259"] },
  { city: "Milan", state: "IL", lat: 41.4262, lng: -90.5739, zips: ["61264"] },
  { city: "Moline", state: "IL", lat: 41.4986, lng: -90.5066, zips: ["61265", "61266"] },
  { city: "Orion", state: "IL", lat: 41.3634, lng: -90.3849, zips: ["61273"] },
  { city: "Osco", state: "IL", lat: 41.3637, lng: -90.2681, zips: ["61274"] },
  { city: "Port Byron", state: "IL", lat: 41.6013, lng: -90.3263, zips: ["61275"] },
  { city: "Rapids City", state: "IL", lat: 41.5817, lng: -90.3435, zips: ["61278"] },
  { city: "Reynolds", state: "IL", lat: 41.3277, lng: -90.6384, zips: ["61279"] },
  { city: "Rock Island", state: "IL", lat: 41.5222, lng: -90.5741, zips: ["61201", "61204", "61299"] },
  { city: "Sherrard", state: "IL", lat: 41.3027, lng: -90.4939, zips: ["61281"] },
  { city: "Silvis", state: "IL", lat: 41.5007, lng: -90.4126, zips: ["61282"] },
  { city: "Taylor Ridge", state: "IL", lat: 41.3828, lng: -90.734, zips: ["61284"] },
];

/**
 * Default radius origin: the heart of the metro (downtown Davenport / Rock
 * Island, across the river), NOT the contractor's business. An edge-of-metro
 * pro (e.g. Eldridge) brushes a radius over the QC core and taps off the
 * rural towns they don't want — the tap is where the "I'll go into the QC but
 * not into the country" judgment lives.
 */
export const QC_CENTER = { lat: 41.51, lng: -90.57 } as const;

/** A stable key for a town (city + state — the metro straddles the state line). */
export function townKey(t: Pick<ServiceTown, "city" | "state">): string {
  return `${t.city}|${t.state}`;
}

const TOWN_BY_KEY = new Map(QC_TOWNS.map((t) => [townKey(t), t]));
const TOWN_BY_ZIP = new Map<string, ServiceTown>();
for (const t of QC_TOWNS) for (const z of t.zips) TOWN_BY_ZIP.set(z, t);

export function getTown(key: string): ServiceTown | undefined {
  return TOWN_BY_KEY.get(key);
}

/** Great-circle distance in miles. */
export function haversineMiles(
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number,
): number {
  const R = 3958.8; // Earth radius, miles
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const lat1 = toRad(aLat);
  const lat2 = toRad(bLat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Town keys whose marker falls within `miles` of (centerLat, centerLng). */
export function townKeysWithinRadius(
  centerLat: number,
  centerLng: number,
  miles: number,
): string[] {
  return QC_TOWNS.filter((t) => haversineMiles(centerLat, centerLng, t.lat, t.lng) <= miles).map(
    townKey,
  );
}

/** Expand selected town keys to the canonical 5-digit ZIP set (sorted, deduped). */
export function zipsForTownKeys(keys: Iterable<string>): string[] {
  const zips = new Set<string>();
  for (const k of keys) {
    const t = TOWN_BY_KEY.get(k);
    if (t) for (const z of t.zips) zips.add(z);
  }
  return [...zips].sort();
}

/** Distinct "City, ST" labels for selected town keys (display only). */
export function cityLabelsForTownKeys(keys: Iterable<string>): string[] {
  const labels: string[] = [];
  for (const k of keys) {
    const t = TOWN_BY_KEY.get(k);
    if (t) labels.push(`${t.city}, ${t.state}`);
  }
  return labels.sort();
}

/**
 * Recover the selected town set from a stored ZIP list (editor load). A town
 * counts as selected if ANY of its ZIPs is present — forgiving toward
 * hand-curated or partial sets, and saving re-expands to the full town.
 */
export function townKeysForZips(zips: Iterable<string>): string[] {
  const keys = new Set<string>();
  for (const z of zips) {
    const t = TOWN_BY_ZIP.get(z);
    if (t) keys.add(townKey(t));
  }
  return [...keys];
}

/** Projection bounds (with a small margin) for laying towns out on the SVG. */
export const QC_VIEW_BOUNDS = (() => {
  const lats = QC_TOWNS.map((t) => t.lat);
  const lngs = QC_TOWNS.map((t) => t.lng);
  const margin = 0.04;
  return {
    minLat: Math.min(...lats) - margin,
    maxLat: Math.max(...lats) + margin,
    minLng: Math.min(...lngs) - margin,
    maxLng: Math.max(...lngs) + margin,
  };
})();
